/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.patients

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.KeyboardArrowUp
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.ui.CauceDateField
import studio.oddpods.cauce.core.ui.asString
import studio.oddpods.cauce.feature.patients.generated.resources.Res
import studio.oddpods.cauce.feature.patients.generated.resources.patients_email_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_fee_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_fee_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_name_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_notes_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_notes_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_phone_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_phone_placeholder
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_anchor_date_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_date_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_biweekly
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_custom
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_monthly
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_frequency_weekly
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_interval_days_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_kind_one_off
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_kind_recurring
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_section_title
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_type_double
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_type_final
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_type_first
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_type_label
import studio.oddpods.cauce.feature.patients.generated.resources.patients_session_type_regular

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddPatientWithSessionContent(
    state: AddPatientWithSessionState,
    dateFormatter: DateFormatter,
    onAction: (AddPatientWithSessionAction) -> Unit,
    modifier: Modifier = Modifier,
    bottomContent: @Composable () -> Unit = {},
) {
    Column(modifier = modifier) {
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState()),
        ) {
            Spacer(modifier = Modifier.height(8.dp))

            // --- Patient fields ---

            OutlinedTextField(
                value = state.name,
                onValueChange = { onAction(AddPatientWithSessionAction.NameChanged(it)) },
                label = { Text(stringResource(Res.string.patients_name_label)) },
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.phone,
                onValueChange = { onAction(AddPatientWithSessionAction.PhoneChanged(it)) },
                label = { Text(stringResource(Res.string.patients_phone_label)) },
                placeholder = { Text(stringResource(Res.string.patients_phone_placeholder)) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.email,
                onValueChange = { onAction(AddPatientWithSessionAction.EmailChanged(it)) },
                label = { Text(stringResource(Res.string.patients_email_label)) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.defaultFeeInput,
                onValueChange = { onAction(AddPatientWithSessionAction.DefaultFeeChanged(it)) },
                label = { Text(stringResource(Res.string.patients_fee_label)) },
                placeholder = { Text(stringResource(Res.string.patients_fee_placeholder)) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )

            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.notes,
                onValueChange = { onAction(AddPatientWithSessionAction.NotesChanged(it)) },
                label = { Text(stringResource(Res.string.patients_notes_label)) },
                placeholder = { Text(stringResource(Res.string.patients_notes_placeholder)) },
                minLines = 3,
                modifier = Modifier.fillMaxWidth(),
            )

            if (state.error != null) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(state.error.asString(), color = MaterialTheme.colorScheme.error)
            }

            Spacer(modifier = Modifier.height(16.dp))

            // --- Session section ---

            HorizontalDivider()

            SessionSectionHeader(
                expanded = state.sessionExpanded,
                onClick = { onAction(AddPatientWithSessionAction.SessionExpandedChanged(!state.sessionExpanded)) },
            )

            AnimatedVisibility(
                visible = state.sessionExpanded,
                enter = expandVertically(),
                exit = shrinkVertically(),
            ) {
                SessionSectionContent(
                    state = state,
                    dateFormatter = dateFormatter,
                    onAction = onAction,
                )
            }

            if (state.sessionError != null) {
                Spacer(modifier = Modifier.height(8.dp))
                Text(state.sessionError.asString(), color = MaterialTheme.colorScheme.error)
            }

            Spacer(modifier = Modifier.height(16.dp))
        }

        bottomContent()
    }
}

@Composable
private fun SessionSectionHeader(
    expanded: Boolean,
    onClick: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 16.dp),
    ) {
        Text(
            text = stringResource(Res.string.patients_session_section_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.weight(1f),
        )
        Icon(
            imageVector = if (expanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
            contentDescription = null,
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SessionSectionContent(
    state: AddPatientWithSessionState,
    dateFormatter: DateFormatter,
    onAction: (AddPatientWithSessionAction) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        SingleChoiceSegmentedButtonRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 8.dp),
        ) {
            SegmentedButton(
                selected = state.sessionKind == SessionKind.ONE_OFF,
                onClick = { onAction(AddPatientWithSessionAction.SessionKindChanged(SessionKind.ONE_OFF)) },
                shape = SegmentedButtonDefaults.itemShape(0, 2),
            ) { Text(stringResource(Res.string.patients_session_kind_one_off)) }
            SegmentedButton(
                selected = state.sessionKind == SessionKind.RECURRING,
                onClick = { onAction(AddPatientWithSessionAction.SessionKindChanged(SessionKind.RECURRING)) },
                shape = SegmentedButtonDefaults.itemShape(1, 2),
            ) { Text(stringResource(Res.string.patients_session_kind_recurring)) }
        }

        Spacer(modifier = Modifier.height(8.dp))

        when (state.sessionKind) {
            SessionKind.ONE_OFF -> OneOffSessionFields(state, dateFormatter, onAction)
            SessionKind.RECURRING -> RecurringSessionFields(state, dateFormatter, onAction)
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun OneOffSessionFields(
    state: AddPatientWithSessionState,
    dateFormatter: DateFormatter,
    onAction: (AddPatientWithSessionAction) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        CauceDateField(
            selectedDate = state.sessionDate,
            onDateSelected = { onAction(AddPatientWithSessionAction.SessionDateChanged(it)) },
            dateFormatter = dateFormatter,
            label = { Text(stringResource(Res.string.patients_session_date_label)) },
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(12.dp))

        SessionTypeDropdown(
            selectedType = state.sessionType,
            onTypeSelected = { onAction(AddPatientWithSessionAction.SessionTypeChanged(it)) },
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun RecurringSessionFields(
    state: AddPatientWithSessionState,
    dateFormatter: DateFormatter,
    onAction: (AddPatientWithSessionAction) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        FrequencyDropdown(
            selectedFrequency = state.frequency,
            onFrequencySelected = { onAction(AddPatientWithSessionAction.FrequencyChanged(it)) },
        )

        Spacer(modifier = Modifier.height(12.dp))

        CauceDateField(
            selectedDate = state.anchorDate,
            onDateSelected = { onAction(AddPatientWithSessionAction.AnchorDateChanged(it)) },
            dateFormatter = dateFormatter,
            label = { Text(stringResource(Res.string.patients_session_anchor_date_label)) },
            modifier = Modifier.fillMaxWidth(),
        )

        if (state.frequency == RecurrenceFrequency.CUSTOM) {
            Spacer(modifier = Modifier.height(12.dp))

            OutlinedTextField(
                value = state.intervalDaysInput,
                onValueChange = { onAction(AddPatientWithSessionAction.IntervalDaysChanged(it)) },
                label = { Text(stringResource(Res.string.patients_session_interval_days_label)) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun SessionTypeDropdown(
    selectedType: SessionType,
    onTypeSelected: (SessionType) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val labels = mapOf(
        SessionType.REGULAR to stringResource(Res.string.patients_session_type_regular),
        SessionType.FIRST to stringResource(Res.string.patients_session_type_first),
        SessionType.FINAL to stringResource(Res.string.patients_session_type_final),
        SessionType.DOUBLE to stringResource(Res.string.patients_session_type_double),
    )

    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it },
    ) {
        OutlinedTextField(
            value = labels[selectedType] ?: selectedType.name,
            onValueChange = {},
            readOnly = true,
            label = { Text(stringResource(Res.string.patients_session_type_label)) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            modifier = Modifier
                .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
                .fillMaxWidth(),
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
        ) {
            SessionType.entries.forEach { type ->
                DropdownMenuItem(
                    text = { Text(labels[type] ?: type.name) },
                    onClick = {
                        onTypeSelected(type)
                        expanded = false
                    },
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun FrequencyDropdown(
    selectedFrequency: RecurrenceFrequency,
    onFrequencySelected: (RecurrenceFrequency) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val labels = mapOf(
        RecurrenceFrequency.WEEKLY to stringResource(Res.string.patients_session_frequency_weekly),
        RecurrenceFrequency.BIWEEKLY to stringResource(Res.string.patients_session_frequency_biweekly),
        RecurrenceFrequency.MONTHLY to stringResource(Res.string.patients_session_frequency_monthly),
        RecurrenceFrequency.CUSTOM to stringResource(Res.string.patients_session_frequency_custom),
    )

    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = it },
    ) {
        OutlinedTextField(
            value = labels[selectedFrequency] ?: selectedFrequency.name,
            onValueChange = {},
            readOnly = true,
            label = { Text(stringResource(Res.string.patients_session_frequency_label)) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            modifier = Modifier
                .menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable)
                .fillMaxWidth(),
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
        ) {
            RecurrenceFrequency.entries.forEach { freq ->
                DropdownMenuItem(
                    text = { Text(labels[freq] ?: freq.name) },
                    onClick = {
                        onFrequencySelected(freq)
                        expanded = false
                    },
                )
            }
        }
    }
}
